import { useLazyQuery, useMutation } from '@apollo/client';
import { Alert, Button, Descriptions, Form, Input, message, Spin } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { humanizeEnum } from '../../common/utils';
import AddDelayReasonModal from '../../libraryModules/orders/modules/all/components/AddDelayReasonModal';
import ModalComponent from '../ModalComponent';
import {
  UNIT_SCAN_MODES,
  UNIT_SCAN_STEPS,
  getBlockReasonMessage,
  isHardBlock,
  isIgnoredBlockReason,
} from './constants';
import {
  CONFIRM_RETURN_ORDER_V2,
  PACK_INVENTORY_UNIT,
} from './graphql/Mutations';
import {
  CHECK_INVENTORY_UNIT_ASSIGNMENT,
  PICKABLE_INVENTORY_UNITS,
  PREVIEW_INVENTORY_UNIT_INSPECTION,
} from './graphql/Queries';
import UnitDamageModal from './UnitDamageModal';
import UnitCodeInput from './UnitCodeInput';
import UnitInspectionForm from './UnitInspectionForm';
import './unitScan.less';
import {
  SHELF_LOCATION_MAX_LENGTH,
  normalizeShelfLocation,
  buildChecklistAnswersInput,
  buildClientRequestId,
  buildPiecesInput,
  getInspectionErrors,
  REQUIRED_FIELD_MESSAGE,
  isCompleteUnitCode,
  normalizeUnitCode,
} from './utils';

const UnitScanModal = ({
  open,
  onClose,
  mode = UNIT_SCAN_MODES?.PICK,
  orderItemId,
  returnOrderItemId,
  returnOrderId,
  toyName,
  onSuccess,
}) => {
  const isPickMode = mode === UNIT_SCAN_MODES?.PICK;

  const formBodyRef = useRef(null);
  const [step, setStep] = useState(UNIT_SCAN_STEPS?.CODE);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [checkData, setCheckData] = useState(null);
  const [pieceCounts, setPieceCounts] = useState({});
  const [checklistAnswers, setChecklistAnswers] = useState({});
  const [notes, setNotes] = useState('');
  const [shelfLocation, setShelfLocation] = useState('');
  const [formError, setFormError] = useState('');
  const [shelfError, setShelfError] = useState('');
  const [isDelayPromptOpen, setIsDelayPromptOpen] = useState(false);
  const [delayForm] = Form.useForm();
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [damagePreview, setDamagePreview] = useState(null);

  const [checkUnit, { loading: isChecking }] = useLazyQuery(
    CHECK_INVENTORY_UNIT_ASSIGNMENT,
    { fetchPolicy: 'no-cache', errorPolicy: 'all', onError() {} },
  );
  const [
    previewInspection,
    { loading: isPreviewing },
  ] = useLazyQuery(PREVIEW_INVENTORY_UNIT_INSPECTION, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    onError() {},
  });
  const [
    getPickableUnits,
    { data: pickableData, loading: isLoadingPickableUnits },
  ] = useLazyQuery(PICKABLE_INVENTORY_UNITS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError() {},
  });
  const [packUnit, { loading: isPacking }] = useMutation(PACK_INVENTORY_UNIT, {
    errorPolicy: 'all',
    onError() {},
  });
  const [
    confirmReturn,
    { loading: isConfirmingReturn },
  ] = useMutation(CONFIRM_RETURN_ORDER_V2, {
    errorPolicy: 'all',
    onError() {},
  });

  const isSubmitting = isPacking || isConfirmingReturn || isPreviewing;
  const pickable = pickableData?.pickableInventoryUnits;
  const isInitialLoading = isPickMode && isLoadingPickableUnits;

  const getApiError = (response, fallback) =>
    response?.errors?.[0]?.message || response?.error?.message || fallback;

  const resetState = () => {
    setStep(UNIT_SCAN_STEPS?.CODE);
    setCode('');
    setCodeError('');
    setCheckData(null);
    setPieceCounts({});
    setChecklistAnswers({});
    setFieldErrors({});
    setNotes('');
    setShelfLocation('');
    setFormError('');
    setShelfError('');
    setIsDelayPromptOpen(false);
    delayForm?.resetFields();
    setSuccessMessage('');
    setDamagePreview(null);
  };

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    if (isPickMode && orderItemId) {
      getPickableUnits({ variables: { where: { id: orderItemId } } });
    }
  }, [open, orderItemId]);

  useEffect(() => {
    if (!successMessage || !formBodyRef?.current) return;
    formBodyRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [successMessage]);

  const getCheckInput = (unitCode) => ({
    ...(isPickMode ? { orderItemId } : { returnOrderItemId }),
    ...(isPickMode || isCompleteUnitCode(unitCode)
      ? { qrCode: unitCode }
      : { returnOrderNumber: unitCode }),
  });

  const applyCheckResult = (data) => {
    setCheckData(data);
    if (
      isHardBlock(data) ||
      (!data?.canAssign && !isIgnoredBlockReason(data))
    ) {
      setStep(UNIT_SCAN_STEPS?.BLOCKED);
      return;
    }
    setPieceCounts({});
    setChecklistAnswers({});
    setFieldErrors({});
    setStep(UNIT_SCAN_STEPS?.FORM);
  };

  const runCheck = async (rawCode) => {
    const unitCode = normalizeUnitCode(rawCode);
    if (!unitCode) return;
    setCodeError('');
    setSuccessMessage('');
    const response = await checkUnit({
      variables: { data: getCheckInput(unitCode) },
    });
    const data = response?.data?.checkInventoryUnitAssignment?.data;
    if (!data) {
      setCodeError(
        getApiError(response, 'Could not verify this unit. Try again.'),
      );
      return;
    }
    applyCheckResult(data);
  };

  const handlePack = async (delayMetadata) => {
    const form = checkData?.inspectionForm;
    const unitCode = checkData?.unit?.qrCode || code;
    const response = await packUnit({
      variables: {
        data: {
          orderItemId: checkData?.orderItemId || orderItemId,
          metadata: delayMetadata,
          qrCode: unitCode,
          unitCode: checkData?.unit?.unitCode,
          pieces: buildPiecesInput(form?.requiredContents, pieceCounts),
          checklist: buildChecklistAnswersInput(
            form?.checklist,
            checklistAnswers,
          ),
          notes: notes?.trim() || undefined,
          clientRequestId: buildClientRequestId('pack', unitCode),
        },
      },
    });
    const data = response?.data?.packInventoryUnit?.data;
    const isFailed = `${data?.result || ''}`?.toUpperCase()?.includes('FAIL');

    if (data?.unitId && !isFailed) {
      if (onSuccess) onSuccess(data);
      if (data?.isOrderItemFulfilled) {
        message?.success(
          data?.message ||
            response?.data?.packInventoryUnit?.message ||
            'All units added. This order item is fulfilled.',
        );
        onClose();
        return true;
      }
      const remaining =
        data?.message ||
        response?.data?.packInventoryUnit?.message ||
        'Unit added to this order item.';
      resetState();
      setSuccessMessage(remaining);
      getPickableUnits({ variables: { where: { id: orderItemId } } });
      return true;
    }
    setFormError(
      data?.message ||
        getApiError(response, 'Could not add this unit. Please try again.'),
    );
    return false;
  };

  const handleReturnInward = async (damage) => {
    const form = checkData?.inspectionForm;
    const unitCode = checkData?.unit?.qrCode || code;
    const response = await confirmReturn({
      variables: {
        where: { id: returnOrderId },
        data: {
          inventoryUnits: [
            {
              returnOrderItemId,
              qrCode: unitCode,
              unitCode: checkData?.unit?.unitCode,
              pieces: buildPiecesInput(form?.requiredContents, pieceCounts),
              checklist: buildChecklistAnswersInput(
                form?.checklist,
                checklistAnswers,
              ),
              notes: notes?.trim() || undefined,
              shelfLocation: shelfLocation || undefined,
              ...(damage?.damageDescription
                ? {
                    damageDescription: damage?.damageDescription,
                    damageImages: damage?.damageImages,
                    ...(damage?.videoKey
                      ? { attachments: [damage?.videoKey] }
                      : {}),
                  }
                : {}),
              clientRequestId: buildClientRequestId('inward', unitCode),
            },
          ],
        },
      },
    });
    const result = response?.data?.confirmReturnOrderV2;
    if (result?.data?.inventoryUnits?.length) {
      if (onSuccess) onSuccess(result?.data?.inventoryUnits?.[0]);
      setCheckData(null);
      setSuccessMessage(result?.message || 'Return confirmed.');
      setStep(UNIT_SCAN_STEPS?.DONE);
      return;
    }
    setFormError(
      getApiError(response, 'Could not confirm this return. Try again.'),
    );
  };

  const isDamageCase = async () => {
    const form = checkData?.inspectionForm;
    const response = await previewInspection({
      variables: {
        data: {
          ...(isPickMode
            ? { orderItemId: checkData?.orderItemId || orderItemId }
            : {
                returnOrderItemId:
                  checkData?.returnOrderItemId || returnOrderItemId,
              }),
          qrCode: checkData?.unit?.qrCode || code,
          unitCode: checkData?.unit?.unitCode,
          pieces: buildPiecesInput(form?.requiredContents, pieceCounts),
          checklist: buildChecklistAnswersInput(
            form?.checklist,
            checklistAnswers,
          ),
        },
      },
    });
    const data = response?.data?.previewInventoryUnitInspection?.data;
    if (!data) {
      setFormError(
        getApiError(response, 'Could not check this unit. Please try again.'),
      );
      return true;
    }
    if (data?.requiresDamageRequest) {
      setDamagePreview(data);
      return true;
    }
    return false;
  };

  const clearFieldError = (key, value) => {
    if (value === undefined || value === null || value === '') return;
    setFieldErrors((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateInspection = () => {
    const errors = getInspectionErrors(
      checkData?.inspectionForm,
      pieceCounts,
      checklistAnswers,
    );
    setFieldErrors(errors);
    return Object?.keys(errors)?.length === 0;
  };

  const needsDelayReason = () =>
    isPickMode &&
    Boolean(checkData?.order?.delayedInConfirmed) &&
    !checkData?.order?.delayReason;

  const handleSubmit = async () => {
    setFormError('');
    const needsShelf = !isPickMode;
    setShelfError(!needsShelf || shelfLocation ? '' : REQUIRED_FIELD_MESSAGE);
    const isInspectionValid = validateInspection();
    if ((needsShelf && !shelfLocation) || !isInspectionValid) return;
    const isDamaged = await isDamageCase();
    if (isDamaged) return;
    if (isPickMode) {
      if (needsDelayReason()) {
        setIsDelayPromptOpen(true);
        return;
      }
      handlePack();
      return;
    }
    handleReturnInward();
  };

  const handleDelaySubmit = async (values) => {
    const isPacked = await handlePack([
      { key: 'delayReason', value: values?.reason },
      { key: 'delayNote', value: values?.comment },
    ]);
    if (!isPacked) return;
    setIsDelayPromptOpen(false);
    delayForm?.resetFields();
  };

  const renderBlocked = () => (
    <>
      <div className="unit-scan-body">
        <Alert
          type={isHardBlock(checkData) ? 'error' : 'warning'}
          showIcon
          message={getBlockReasonMessage(checkData)}
        />
        {checkData?.unit && (
          <Descriptions className="mt-16" size="small" column={1} bordered>
            <Descriptions.Item label="Unit">
              {checkData?.unit?.unitCode || checkData?.unit?.qrCode || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {humanizeEnum(checkData?.unit?.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Toy">
              {checkData?.unit?.toy?.name || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </div>
      <div className="unit-scan-actions">
        <Button onClick={resetState}>Enter a different code</Button>
      </div>
    </>
  );

  const renderForm = () => (
    <>
      <div className="unit-scan-body" ref={formBodyRef}>
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Unit">
            {checkData?.unit?.unitCode || checkData?.unit?.qrCode || code}
          </Descriptions.Item>
          <Descriptions.Item label="Toy">
            {checkData?.unit?.toy?.name || toyName || '-'}
          </Descriptions.Item>
        </Descriptions>
        {!isPickMode && (
          <div className="unit-scan-shelf">
            <div className="unit-scan-section-title">Shelf location</div>
            <Input
              allowClear
              maxLength={SHELF_LOCATION_MAX_LENGTH}
              placeholder="e.g. A1, B12, R2S3"
              value={shelfLocation}
              disabled={isSubmitting}
              status={shelfError ? 'error' : undefined}
              onChange={(event) => {
                const value = normalizeShelfLocation(event?.target?.value);
                setShelfLocation(value);
                if (value) setShelfError('');
              }}
            />
            {shelfError && <div className="unit-scan-error">{shelfError}</div>}
          </div>
        )}
        <UnitInspectionForm
          inspectionForm={checkData?.inspectionForm}
          pieceCounts={pieceCounts}
          onPieceCountChange={(key, value) => {
            setPieceCounts((prev) => ({ ...prev, [key]: value }));
            clearFieldError(key, value);
          }}
          checklistAnswers={checklistAnswers}
          onChecklistAnswerChange={(key, value) => {
            setChecklistAnswers((prev) => ({ ...prev, [key]: value }));
            clearFieldError(key, value);
          }}
          errors={fieldErrors}
          notes={notes}
          onNotesChange={setNotes}
          disabled={isSubmitting}
        />
      </div>
      <div className="unit-scan-actions">
        <Button className="mr-16" onClick={resetState} disabled={isSubmitting}>
          Enter a different code
        </Button>
        <Button
          type="primary"
          className="common-button"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {isPickMode ? 'Proceed to Confirm Order' : 'Confirm return'}
        </Button>
      </div>
    </>
  );

  const renderDone = () => (
    <>
      <div className="unit-scan-body">
        <Alert type="success" showIcon message={successMessage} />
      </div>
      <div className="unit-scan-actions">
        <Button type="primary" className="common-button" onClick={onClose}>
          Close
        </Button>
      </div>
    </>
  );

  return (
    <>
      <ModalComponent
        title={isPickMode ? 'Pick unit' : 'Confirm returned unit'}
        open={open}
        onCancel={onClose}
        footer={null}
        width={880}
        className="modal unit-scan-modal"
        destroyOnClose
      >
        <div className="unit-scan">
          {formError && (
            <Alert
              className="unit-scan-banner"
              type="error"
              showIcon
              message={formError}
            />
          )}
          {successMessage && step !== UNIT_SCAN_STEPS?.DONE && (
            <Alert
              className="unit-scan-banner"
              type="success"
              showIcon
              message={successMessage}
            />
          )}
          {step === UNIT_SCAN_STEPS?.CODE && isInitialLoading && (
            <div className="unit-scan-loading">
              <Spin />
            </div>
          )}
          {step === UNIT_SCAN_STEPS?.CODE && !isInitialLoading && (
            <UnitCodeInput
              value={code}
              onChange={setCode}
              onSubmit={runCheck}
              loading={isChecking}
              error={codeError}
            showContinue={!isPickMode}
            hint={
              isPickMode
                ? undefined
                : 'No QR on the unit? Enter the product return ID instead, e.g. RT539, and press Continue.'
            }
              suggestions={
                isPickMode
                  ? (pickable?.data || [])
                      ?.filter((unit) => unit?.isPickable)
                      ?.map((unit) => unit?.qrCode)
                      ?.filter(Boolean)
                  : []
              }
            />
          )}
          {step === UNIT_SCAN_STEPS?.BLOCKED && renderBlocked()}
          {step === UNIT_SCAN_STEPS?.FORM && renderForm()}
          {step === UNIT_SCAN_STEPS?.DONE && renderDone()}
        </div>
      </ModalComponent>
      {isDelayPromptOpen && (
        <AddDelayReasonModal
          form={delayForm}
          isModalOpen={isDelayPromptOpen}
          setIsModalOpen={setIsDelayPromptOpen}
          isBtnLoading={isPacking}
          onFinish={handleDelaySubmit}
        />
      )}
      <UnitDamageModal
        open={Boolean(damagePreview)}
        preview={damagePreview}
        unit={checkData?.unit}
        orderItemId={isPickMode ? orderItemId : undefined}
        inspectionForm={checkData?.inspectionForm}
        pieceCounts={pieceCounts}
        checklistAnswers={checklistAnswers}
        notes={notes}
        onClose={() => setDamagePreview(null)}
        onDamageDetails={
          isPickMode
            ? undefined
            : (damage) => {
                setDamagePreview(null);
                handleReturnInward(damage);
              }
        }
        onDamaged={(damageMessage) => {
          setDamagePreview(null);
          setCheckData(null);
          if (onSuccess) onSuccess(null);
          setSuccessMessage(damageMessage);
          setStep(UNIT_SCAN_STEPS?.DONE);
        }}
      />
    </>
  );
};

export default UnitScanModal;
